import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * Status configuration dictionary defining style classes,
 * indicator dot type, and pulse glow animation requirement.
 */
const statusConfig = {
  // Red / Critical / High / Blocked / Deny / Fail / Open
  critical: {
    style: "bg-red-950/50 text-red-400 border-red-500/50 shadow-[0_0_12px_rgba(239,68,68,0.35)]",
    dot: "critical",
    pulse: true,
  },
  high: {
    style: "bg-red-950/50 text-red-400 border-red-500/50 shadow-[0_0_12px_rgba(239,68,68,0.35)]",
    dot: "critical",
    pulse: true,
  },
  blocked: {
    style: "bg-rose-950/50 text-rose-400 border-rose-500/50 shadow-[0_0_10px_rgba(244,63,94,0.3)]",
    dot: "critical",
    pulse: false,
  },
  deny: {
    style: "bg-rose-950/50 text-rose-400 border-rose-500/50 shadow-[0_0_10px_rgba(244,63,94,0.3)]",
    dot: "critical",
    pulse: false,
  },
  fail: {
    style: "bg-rose-950/50 text-rose-400 border-rose-500/50 shadow-[0_0_10px_rgba(244,63,94,0.3)]",
    dot: "critical",
    pulse: false,
  },
  open: {
    style: "bg-rose-950/50 text-rose-400 border-rose-500/50 shadow-[0_0_10px_rgba(244,63,94,0.3)]",
    dot: "critical",
    pulse: false,
  },

  // Amber / Orange / Warning / Suspicious
  warning: {
    style: "bg-amber-950/50 text-amber-400 border-amber-500/50 shadow-[0_0_10px_rgba(245,158,11,0.3)]",
    dot: "live",
    pulse: false,
  },
  warn: {
    style: "bg-amber-950/50 text-amber-400 border-amber-500/50 shadow-[0_0_10px_rgba(245,158,11,0.3)]",
    dot: "live",
    pulse: false,
  },
  suspicious: {
    style: "bg-amber-950/50 text-amber-400 border-amber-500/50 shadow-[0_0_10px_rgba(245,158,11,0.3)]",
    dot: "live",
    pulse: false,
  },
  medium: {
    style: "bg-amber-950/50 text-amber-400 border-amber-500/50 shadow-[0_0_10px_rgba(245,158,11,0.3)]",
    dot: "live",
    pulse: false,
  },

  // Green / Allow / Pass / Trusted / Success
  allow: {
    style: "bg-emerald-950/50 text-emerald-400 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.3)]",
    dot: "live",
    pulse: false,
  },
  pass: {
    style: "bg-emerald-950/50 text-emerald-400 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.3)]",
    dot: "live",
    pulse: false,
  },
  trusted: {
    style: "bg-emerald-950/50 text-emerald-400 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.3)]",
    dot: "live",
    pulse: false,
  },
  success: {
    style: "bg-emerald-950/50 text-emerald-400 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.3)]",
    dot: "live",
    pulse: false,
  },
  low: {
    style: "bg-emerald-950/50 text-emerald-400 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.3)]",
    dot: "live",
    pulse: false,
  },
  ok: {
    style: "bg-emerald-950/50 text-emerald-400 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.3)]",
    dot: "live",
    pulse: false,
  },

  // Cyan / Info / Acknowledged
  info: {
    style: "bg-cyan-950/50 text-cyan-400 border-cyan-500/50 shadow-[0_0_10px_rgba(6,182,212,0.3)]",
    dot: "live",
    pulse: false,
  },
  acknowledged: {
    style: "bg-cyan-950/50 text-cyan-400 border-cyan-500/50 shadow-[0_0_10px_rgba(6,182,212,0.3)]",
    dot: "live",
    pulse: false,
  },

  // Purple / MFA / JIT
  mfa: {
    style: "bg-purple-950/50 text-purple-400 border-purple-500/50 shadow-[0_0_10px_rgba(168,85,247,0.3)]",
    dot: "live",
    pulse: false,
  },
  jit: {
    style: "bg-purple-950/50 text-purple-400 border-purple-500/50 shadow-[0_0_10px_rgba(168,85,247,0.3)]",
    dot: "live",
    pulse: false,
  },
};

const defaultConfig = {
  style: "bg-slate-900/60 text-slate-300 border-slate-700/50 shadow-[0_0_8px_rgba(148,163,184,0.15)]",
  dot: "live",
  pulse: false,
};

export function StatusBadge({ status, children, className, ...rest }) {
  const key = String(status || "").toLowerCase();
  const config = statusConfig[key] || defaultConfig;

  return (
    <motion.span
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.16em] backdrop-blur-md transition-all duration-300",
        config.style,
        config.pulse && "[animation:pulse-glow_2s_ease-in-out_infinite]",
        className
      )}
      style={{
        ...(config.pulse ? { animation: "pulse-glow 2s ease-in-out infinite" } : {}),
        ...rest.style,
      }}
      {...rest}
    >
      {config.dot === "critical" ? (
        <span className="critical-dot shrink-0" />
      ) : config.dot === "live" ? (
        <span className="live-dot shrink-0" />
      ) : (
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-current shrink-0" />
      )}
      <span>{children || status}</span>
    </motion.span>
  );
}
